import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Chip from "@mui/material/Chip"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogContentText from "@mui/material/DialogContentText"
import DialogTitle from "@mui/material/DialogTitle"
import Skeleton from "@mui/material/Skeleton"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Typography from "@mui/material/Typography"
import { format as formatDate } from "date-fns"
import { capitalize } from "lodash"
import { useCallback, useEffect, useState } from "react"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import ConditionalPurpleLinearProgress from "../../../common/components/ConditionalLinearProgress"
import ScrollableTableContainer from "../../../common/components/tables/ScrollableTableContainer"
import {
  HeaderTableCell,
  NoWrapTableCell,
  NumberTableCell,
  StickyHeaderTableCell,
} from "../../../common/components/tables/cells"
import { formatCurrencyLong } from "../../../common/utils"
import BorderedGrayChip from "../components/BorderedGrayChip"
import { mlsPendingActivationMessage } from "../my-mls/mlsPurchaseActivationCopy"
import SettingsSectionCard from "./SettingsSectionCard"

function formatDateTime(value) {
  if (!value) {
    return "—"
  }
  try {
    return formatDate(new Date(value), "MMM d, yyyy h:mm a")
  } catch {
    return "—"
  }
}

function formatDateOnly(value) {
  if (!value) {
    return "—"
  }
  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split("-").map(Number)
      return formatDate(new Date(year, month - 1, day), "MMM d, yyyy")
    }
    return formatDate(new Date(value), "MMM d, yyyy")
  } catch {
    return "—"
  }
}

function mlsNamesLabel(row) {
  const names = (row.mls_names || []).filter(Boolean)
  return names.length ? names.join(", ") : "this MLS subscription"
}

function subscriptionAccessEndDateLabel(termEndsAt) {
  const formatted = formatDateOnly(termEndsAt)
  return formatted === "—" ? null : formatted
}

/** First calendar day after paid-through date (when MLS access stops). */
function mlsAccessStopsOnLabel(termEndsAt) {
  if (!termEndsAt || !/^\d{4}-\d{2}-\d{2}$/.test(termEndsAt)) {
    return null
  }
  const [year, month, day] = termEndsAt.split("-").map(Number)
  const nextDay = new Date(year, month - 1, day)
  nextDay.setDate(nextDay.getDate() + 1)
  try {
    return formatDate(nextDay, "MMM d, yyyy")
  } catch {
    return null
  }
}

function subscriptionStatusChip(row) {
  if (row.subscription_cancel_at_end) {
    const endLabel = subscriptionAccessEndDateLabel(
      row.subscription_term_ends_at
    )
    const label = endLabel
      ? `MLS access ends ${endLabel}`
      : "Renewal cancelled at term end"
    return (
      <Chip
        size="small"
        label={label}
        color="warning"
        title={
          endLabel
            ? `You can use these MLS through ${endLabel}. After that date you will no longer have access under this subscription.`
            : undefined
        }
      />
    )
  }
  const status = row.zoho_subscription_status
  if (status) {
    return <BorderedGrayChip label={capitalize(status.replace(/_/g, " "))} />
  }
  if (row.status === "pending_checkout") {
    return <BorderedGrayChip label="Checkout pending" />
  }
  if (row.status === "awaiting_payment") {
    return <BorderedGrayChip label="Awaiting payment" />
  }
  return <BorderedGrayChip label={capitalize(row.status)} />
}

function activationLabel(row) {
  if (row.mls_activated_at) {
    return formatDateTime(row.mls_activated_at)
  }
  if (row.status === "paid" && row.assignment_status === "pending") {
    return mlsPendingActivationMessage(row.assign_after)
  }
  return "—"
}

export default function MlsPurchaseHistoryTab() {
  const axios = useAuthorizedAxiosClient()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [cancelTarget, setCancelTarget] = useState(null)
  const [cancelLoading, setCancelLoading] = useState(false)
  const [cancelError, setCancelError] = useState(null)

  const loadPurchases = useCallback(() => {
    setLoading(true)
    setError(null)
    axios
      .get("/billing/api/mls-purchases/")
      .then(({ data }) => setRows(data))
      .catch(() => setError("Could not load MLS purchase history."))
      .finally(() => setLoading(false))
  }, [axios])

  useEffect(() => {
    loadPurchases()
  }, [loadPurchases])

  function handleCancelConfirm() {
    if (!cancelTarget) {
      return
    }
    setCancelLoading(true)
    setCancelError(null)
    axios
      .post(`/billing/api/mls-purchases/${cancelTarget.id}/cancel/`)
      .then(({ data }) => {
        setRows((prev) => prev.map((row) => (row.id === data.id ? data : row)))
        setCancelTarget(null)
      })
      .catch((err) => {
        setCancelError(
          err.response?.data?.detail ||
            "Could not cancel subscription. Please contact support."
        )
      })
      .finally(() => setCancelLoading(false))
  }

  return (
    <Column sx={{ gap: 2 }}>
      <SettingsSectionCard sx={{ gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Each MLS checkout is a separate monthly subscription in Zoho Billing.
          After MLS are activated on your account, you can turn off auto-renewal
          at the end of the current paid term.
        </Typography>
        {error ? <Alert severity="error">{error}</Alert> : null}
        <ConditionalPurpleLinearProgress loading={loading} />
        {!loading && rows.length === 0 ? (
          <Typography color="text.secondary">
            No MLS purchases yet. Use Purchase MLS on the My MLS page to add
            coverage.
          </Typography>
        ) : null}
        {rows.length > 0 ? (
          <ScrollableTableContainer>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <StickyHeaderTableCell>Purchased</StickyHeaderTableCell>
                  <StickyHeaderTableCell>MLS</StickyHeaderTableCell>
                  <StickyHeaderTableCell align="right">
                    Qty
                  </StickyHeaderTableCell>
                  <StickyHeaderTableCell>Duration</StickyHeaderTableCell>
                  <StickyHeaderTableCell>MLS activated</StickyHeaderTableCell>
                  <StickyHeaderTableCell>Paid through</StickyHeaderTableCell>
                  <StickyHeaderTableCell align="right">
                    Monthly
                  </StickyHeaderTableCell>
                  <StickyHeaderTableCell>Status</StickyHeaderTableCell>
                  <StickyHeaderTableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {loading
                  ? [1, 2, 3].map((key) => (
                      <TableRow key={key}>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((cell) => (
                          <NoWrapTableCell key={cell}>
                            <Skeleton />
                          </NoWrapTableCell>
                        ))}
                      </TableRow>
                    ))
                  : rows.map((row) => (
                      <TableRow key={row.id} hover>
                        <NoWrapTableCell>
                          {formatDateTime(row.paid_at || row.created)}
                        </NoWrapTableCell>
                        <NoWrapTableCell>
                          {(row.mls_names || []).filter(Boolean).join(", ") ||
                            "—"}
                        </NoWrapTableCell>
                        <NumberTableCell>{row.quantity}</NumberTableCell>
                        <NoWrapTableCell>
                          {row.billing_duration || "Monthly"}
                        </NoWrapTableCell>
                        <NoWrapTableCell>
                          {activationLabel(row)}
                        </NoWrapTableCell>
                        <NoWrapTableCell>
                          {formatDateOnly(row.subscription_term_ends_at)}
                        </NoWrapTableCell>
                        <NumberTableCell>
                          {row.total_monthly != null
                            ? formatCurrencyLong(row.total_monthly)
                            : row.credits_applied > 0
                            ? "Covered by plan"
                            : "—"}
                        </NumberTableCell>
                        <NoWrapTableCell>
                          {subscriptionStatusChip(row)}
                        </NoWrapTableCell>
                        <NoWrapTableCell align="right">
                          {row.can_cancel_subscription ? (
                            <LoadingButton
                              size="small"
                              variant="outlined"
                              color="warning"
                              onClick={() => setCancelTarget(row)}
                            >
                              Cancel renewal
                            </LoadingButton>
                          ) : null}
                        </NoWrapTableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </ScrollableTableContainer>
        ) : null}
      </SettingsSectionCard>

      <Dialog
        open={Boolean(cancelTarget)}
        onClose={() => !cancelLoading && setCancelTarget(null)}
      >
        <DialogTitle>Cancel MLS subscription renewal?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {cancelTarget ? (
              <>
                <Typography variant="body2" paragraph sx={{ mb: 1.5 }}>
                  You are turning off auto-renewal for{" "}
                  <strong>{mlsNamesLabel(cancelTarget)}</strong>.
                </Typography>
                {subscriptionAccessEndDateLabel(
                  cancelTarget.subscription_term_ends_at
                ) ? (
                  <>
                    <Typography variant="body2" paragraph sx={{ mb: 1.5 }}>
                      Your subscription stays active through the end of{" "}
                      <strong>
                        {subscriptionAccessEndDateLabel(
                          cancelTarget.subscription_term_ends_at
                        )}
                      </strong>
                      . You can keep using these MLS until then.
                    </Typography>
                    <Typography variant="body2" paragraph sx={{ mb: 0 }}>
                      Starting{" "}
                      <strong>
                        {mlsAccessStopsOnLabel(
                          cancelTarget.subscription_term_ends_at
                        ) ||
                          `the day after ${subscriptionAccessEndDateLabel(
                            cancelTarget.subscription_term_ends_at
                          )}`}
                      </strong>
                      , you will{" "}
                      <strong>no longer have access to these MLS</strong> under
                      this subscription unless you purchase again.
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2" paragraph sx={{ mb: 0 }}>
                    Your MLS stay active through the end of your current paid
                    billing period. After that, this subscription will not renew
                    and you will no longer have access to these MLS under this
                    order.
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1.5 }}
                >
                  You will not be charged again for this subscription unless you
                  purchase again.
                </Typography>
              </>
            ) : null}
          </DialogContentText>
          {cancelError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {cancelError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <LoadingButton
            onClick={() => setCancelTarget(null)}
            disabled={cancelLoading}
          >
            Keep renewing
          </LoadingButton>
          <LoadingButton
            loading={cancelLoading}
            variant="contained"
            color="warning"
            onClick={handleCancelConfirm}
          >
            Cancel at term end
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Column>
  )
}
